'use client';

import React, { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslations } from '@/i18n/use-translations';

interface ProductRichTextProps {
  richText: string;
  detail: {
    intro?: string;
    is_glass?: number;
  };
  SDProductDetailsPageProductPicExp?: (progress: number) => void;
}

const AnchorItem = ({
  progress,
  SDProductDetailsPageProductPicExp,
}: {
  progress: number;
  SDProductDetailsPageProductPicExp?: (progress: number) => void;
}) => {
  const [anchorRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      const expProgress = progress / 100;
      SDProductDetailsPageProductPicExp?.(expProgress);
    }
  }, [inView]);

  return (
    <div
      className="absolute h-[1px] w-[1px] right-[1px]"
      ref={anchorRef}
      style={{ top: `${progress}%` }}
    />
  );
};

const ProductRichText: React.FC<ProductRichTextProps> = ({
  richText,
  detail,
  SDProductDetailsPageProductPicExp,
}) => {
  // 商品不是眼镜的话，商品简介和图文详情的页面样式与眼镜有所不同，而且还需要在图文详情上方展示出商品简介
  const isNotGlass = useMemo(() => detail.is_glass === 2, [detail.is_glass]);
  const t = useTranslations('goodsDetail');

  // 是否展示此模块，有富文本、或者非镜框时有商品简介
  const showThisModule = useMemo(
    () => richText || (detail.intro && isNotGlass),
    [richText, detail.intro, isNotGlass],
  );

  if (!showThisModule) {
    return null;
  }

  return (
    <div
      className={`rich-text-wrap relative mx-auto mt-[2.14vw] mb-[2vw] max-w-[95vw] text-black text-14 pb-[60px] ${
        isNotGlass ? 'not-glass bg-[#f5f5f5] py-[3.75vw] px-[15.73vw]' : ''
      }`}
    >
      <p className="text-[32px] font-bb text-center mb-[32px] leading-[39px]">
        {t('designInspirationDetails')}
      </p>
      {/* 非眼镜商品显示商品简介 */}
      {detail.intro && isNotGlass && (
        <div
          dangerouslySetInnerHTML={{ __html: detail.intro }}
          className="rich-content [&_div]:overflow-hidden [&_img]:max-w-full [&_img]:object-fill [&_p]:flex [&_p]:items-center [&_p]:justify-center [&_p]:flex-col"
        />
      )}

      {/* 富文本内容 */}
      {richText && (
        <div
          dangerouslySetInnerHTML={{ __html: richText }}
          className="rich-content [&_div]:overflow-hidden [&_img]:max-w-full [&_img]:object-fill [&_p]:flex [&_p]:items-center [&_p]:justify-center [&_p]:flex-col"
        />
      )}

      {/* 埋点锚点 - 用于统计用户浏览进度 */}
      {[0, 25, 50, 75, 100].map((item, index) => (
        <AnchorItem
          key={index}
          progress={item}
          SDProductDetailsPageProductPicExp={SDProductDetailsPageProductPicExp}
        />
      ))}
    </div>
  );
};

export default ProductRichText;
